<template>
  <Head title="Accueil - Lyra Events">
    <meta name="description" content="Lyra Events, agence spécialisée dans l'organisation de mariages et d'événements sur-mesure partout en France. Wedding planner, coordinateur et prestataire tout-en-un." />
  </Head>

  <div class="min-h-screen bg-background">
    <!-- AppHeader - visible uniquement au scroll -->
    <div v-show="isScrolled" class="fixed top-0 left-0 right-0 z-50 animate-in slide-in-from-top duration-300">
      <AppHeader :breadcrumbs="[]" />
    </div>

    <!-- Hero Section -->
    <section class="relative h-screen w-full flex items-center justify-center">
        <!-- Background Image -->
        <div class="absolute inset-0">
          <img src="/images/hero-bar.jpg" alt="Lyra Events" class="w-full h-full object-cover" />
          <div class="absolute inset-0 bg-[#123024]/60"></div>
        </div>

        <!-- Navigation flottante - visible uniquement quand pas de scroll -->
        <nav v-show="!isScrolled" class="absolute top-4 sm:top-8 left-0 right-0 z-20" data-aos="fade-down" data-aos-delay="200">
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="space-y-3 sm:space-y-4">
              <!-- Barre de navigation avec hamburger et bouton -->
              <div class="flex items-center justify-between md:justify-end">
                <!-- Hamburger menu - visible uniquement sur mobile -->
                <button
                  @click="mobileMenuOpen = !mobileMenuOpen"
                  class="md:hidden p-2 text-white hover:text-white/80 transition-colors"
                  aria-label="Menu"
                >
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>

                <!-- Bouton en haut à droite -->
                <div>
                  <Link v-if="!isLoggedIn" :href="route('login')">
                    <Button variant="outline" size="sm" class="text-xs sm:text-base sm:size-lg border-2 border-white text-white hover:bg-white hover:text-primary">
                      <span class="hidden sm:inline">Devenir membre/se connecter</span>
                      <span class="sm:hidden">Connexion</span>
                    </Button>
                  </Link>
                  <Link v-else :href="route('profile.edit')">
                    <Button variant="outline" size="sm" class="text-xs sm:text-base sm:size-lg border-2 border-white text-white hover:bg-white hover:text-primary">
                      Mon compte
                    </Button>
                  </Link>
                </div>
              </div>

              <!-- Menu mobile déroulant -->
              <div
                v-if="mobileMenuOpen"
                class="md:hidden space-y-3 animate-in slide-in-from-top"
              >
                <Link
                  v-for="link in navLinks"
                  :key="link.label"
                  :href="link.isRoute ? route(link.href) : link.href"
                  class="block text-white text-lg font-semibold hover:text-white/80 transition-colors py-2"
                  @click="mobileMenuOpen = false"
                >
                  {{ link.label }}
                </Link>
              </div>

              <!-- Liens de navigation centrés en dessous (desktop seulement) -->
              <div class="hidden md:flex items-center justify-center space-x-8">
                <Link
                  v-for="link in navLinks"
                  :key="link.label"
                  :href="link.isRoute ? route(link.href) : link.href"
                  :class="navLinkClass"
                >
                  {{ link.label }}
                </Link>
              </div>
            </div>
          </div>
        </nav>

        <!-- Content -->
        <div class="relative z-10 text-center px-4 mt-32">
          <!-- Logo -->
          <div class="flex justify-center mb-8" data-aos="fade-up" data-aos-duration="1000">
            <img src="/logo.svg" alt="Lyra Events Logo" class="h-80 md:h-[28rem] w-auto" />
          </div>
        </div>
      </section>

      <!-- About Section -->
      <section class="py-24 px-4">
        <div class="max-w-7xl mx-auto">
          <!-- Section Header -->
          <div class="text-center mb-20">
            <p class="text-white text-sm uppercase tracking-widest mb-4 font-serif" data-aos="fade-up">Notre histoire</p>
            <h2 class="text-4xl md:text-5xl font-bold text-white mb-6 flex items-center justify-center gap-4 font-serif" data-aos="fade-up" data-aos-delay="100">
              <span class="h-px w-16 bg-accent"></span>
              <span>À PROPOS DE NOUS</span>
              <span class="h-px w-16 bg-accent"></span>
            </h2>
            <p class="text-muted-foreground text-lg max-w-2xl mx-auto mt-8" data-aos="fade-up" data-aos-delay="200">
              Lyra Events, votre agence spécialisée dans l'organisation de mariages et d'événements sur-mesure partout en France. Wedding planner, coordinateur et prestataire tout-en-un : nous créons des moments inoubliables où que vous soyez.
            </p>
          </div>

          <!-- Content Description -->
          <div class="max-w-4xl mx-auto mb-20 space-y-6 text-muted-foreground leading-relaxed" data-aos="fade-up" data-aos-delay="300">
            <p class="text-lg">
              Depuis sa création, Lyra Events s'est imposée comme une référence incontournable dans l'organisation de mariages et d'événements privés partout en France. Notre approche unique allie l'expertise d'une agence événementielle professionnelle à l'écoute personnalisée et au souci du détail qui font toute la différence.
            </p>
            
            <div class="grid md:grid-cols-2 gap-8 mt-12">
              <div class="space-y-6">
                <div>
                  <h3 class="text-white font-bold text-xl mb-3 flex items-center gap-2">
                    <span class="text-accent text-2xl">✓</span> Accompagnement clé en main
                  </h3>
                  <p>Organisation 100% tout inclus, de la conception à la réalisation. Nous gérons chaque détail pour que vous profitiez pleinement de votre événement sans le stress des préparatifs.</p>
                </div>
                
                <div>
                  <h3 class="text-white font-bold text-xl mb-3 flex items-center gap-2">
                    <span class="text-accent text-2xl">✓</span> Réseau de prestataires fiable
                  </h3>
                  <p>Que vous ayez déjà vos prestataires ou non, nous coordonnons l'ensemble des intervenants (traiteur, DJ, photographe...) pour garantir une cohérence parfaite et une qualité irréprochable.</p>
                </div>
                
                <div>
                  <h3 class="text-white font-bold text-xl mb-3 flex items-center gap-2">
                    <span class="text-accent text-2xl">✓</span> Expertise reconnue
                  </h3>
                  <p>Forte de nombreux mariages et événements réalisés, notre agence a développé des méthodes précises et efficaces qui sécurisent chaque étape de votre projet.</p>
                </div>
              </div>
              
              <div class="space-y-6">
                <div>
                  <h3 class="text-white font-bold text-xl mb-3 flex items-center gap-2">
                    <span class="text-accent text-2xl">✓</span> Service traiteur intégré
                  </h3>
                  <p>Contrairement à beaucoup d'agences qui sous-traitent, nous proposons directement notre service traiteur, garantissant une maîtrise totale de la qualité culinaire et du déroulé le jour J.</p>
                </div>
                
                <div>
                  <h3 class="text-white font-bold text-xl mb-3 flex items-center gap-2">
                    <span class="text-accent text-2xl">✓</span> ADN festif et sur-mesure
                  </h3>
                  <p>Nous ne vendons pas qu'une organisation, mais une ambiance et une expérience unique, adaptée à votre personnalité. Chaque mariage, chaque événement devient votre signature.</p>
                </div>
                
                <div>
                  <h3 class="text-white font-bold text-xl mb-3 flex items-center gap-2">
                    <span class="text-accent text-2xl">✓</span> Flexibilité et réactivité
                  </h3>
                  <p>Nous nous adaptons à vos contraintes, votre budget et vos délais. Notre équipe reste disponible et réactive tout au long du projet pour répondre à vos besoins et ajuster chaque détail.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Images Grid -->
          <div class="grid md:grid-cols-3 gap-8 items-center">
            <!-- Images with gold backdrop -->
            <template v-for="(image, index) in aboutImages" :key="image.src">
              <div class="relative" data-aos="fade-up" :data-aos-delay="index * 200">
                <div class="absolute -inset-2 bg-accent rounded-lg"></div>
                <div class="relative group overflow-hidden rounded-lg bg-background">
                  <img
                    :src="image.src"
                    :alt="image.alt"
                    class="w-full h-[500px] object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
              </div>

              <!-- Center Decorative Pattern (after first image) -->
              <div v-if="index === 0" class="hidden md:flex items-center justify-center" data-aos="zoom-in" data-aos-delay="100">
                <img src="/images/about-middle.svg" alt="Pattern décoratif" class="w-2/3 h-auto" />
              </div>
            </template>
          </div>

          <!-- Video Section -->
          <div class="mt-24">
            <div class="text-center mb-12">
              <p class="text-white text-sm uppercase tracking-widest mb-4 font-serif" data-aos="fade-up">Presse</p>
              <h3 class="text-4xl md:text-5xl font-bold text-white mb-6 flex items-center justify-center gap-4 font-serif" data-aos="fade-up" data-aos-delay="100">
                <span class="h-px w-16 bg-accent"></span>
                <span>ILS PARLENT DE NOUS</span>
                <span class="h-px w-16 bg-accent"></span>
              </h3>
              <p class="text-muted-foreground text-lg max-w-2xl mx-auto" data-aos="fade-up" data-aos-delay="200">
                Reportage CNews : "Le mariage, l'événement le plus important de la vie"
              </p>
            </div>

            <div class="max-w-4xl mx-auto" data-aos="fade-up" data-aos-delay="300">
              <div class="relative">
                <div class="absolute -inset-2 bg-accent rounded-lg"></div>
                <div class="relative rounded-lg overflow-hidden bg-background">
                  <div class="aspect-video">
                    <iframe
                      src="https://www.youtube.com/embed/QlRrVoe1CJg?autoplay=0&rel=0"
                      class="w-full h-full"
                      frameborder="0"
                      allow="clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowfullscreen
                      title="Lyra Events sur CNews - Le mariage, l'événement le plus important de la vie"
                      loading="lazy"
                    ></iframe>
                  </div>
                </div>
              </div>
              <div class="text-center mt-8">
                <a 
                  href="https://www.cnews.fr/videos/le-corner-partenaires/2024-09-12/lyra-events-le-mariage-levenement-le-plus-important-de-la"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-semibold"
                >
                  <span>Lire l'article complet sur CNews</span>
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Events Section -->
      <section class="py-24 px-4">
        <div class="max-w-7xl mx-auto">
          <!-- Section Header -->
          <div class="text-center mb-16">
            <p class="text-white text-sm uppercase tracking-widest mb-4 font-serif" data-aos="fade-up">Nos événements</p>
            <h2 class="text-4xl md:text-5xl font-bold text-white mb-6 flex items-center justify-center gap-4 font-serif" data-aos="fade-up" data-aos-delay="100">
              <span class="h-px w-16 bg-accent"></span>
              <span>PROCHAINS ÉVÉNEMENTS</span>
              <span class="h-px w-16 bg-accent"></span>
            </h2>
            <p class="text-muted-foreground text-lg max-w-2xl mx-auto mt-8" data-aos="fade-up" data-aos-delay="200">
              Soirées à thème, concerts live, DJ sets et événements privés : découvrez notre programmation et réservez votre place pour des moments inoubliables.
            </p>
          </div>

          <!-- Events Grid -->
          <div v-if="upcomingEvents && upcomingEvents.length > 0" class="grid md:grid-cols-3 gap-8 mb-12">
            <div
              v-for="(event, index) in upcomingEvents"
              :key="event.id"
              class="group"
              data-aos="fade-up"
              :data-aos-delay="index * 100"
            >
              <Card class="overflow-hidden h-full hover:border-accent transition-all duration-300">
                <!-- Event Image -->
                <div class="relative h-64 overflow-hidden bg-muted">
                  <img
                    v-if="event.image"
                    :src="event.image"
                    :alt="event.title"
                    class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div v-else class="w-full h-full flex items-center justify-center">
                    <span class="text-muted-foreground">Aucune image</span>
                  </div>
                  <!-- Date Badge -->
                  <div class="absolute top-4 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg font-bold shadow-lg">
                    {{ event.formatted_date }}
                  </div>
                </div>

                <!-- Event Content -->
                <div class="p-6">
                  <h3 class="text-2xl font-bold mb-3 group-hover:text-accent transition-colors">
                    {{ event.title }}
                  </h3>
                  <p class="text-muted-foreground mb-4 line-clamp-3">
                    {{ event.description }}
                  </p>
                  <div class="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <div class="flex items-center gap-1">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {{ event.formatted_time }}
                    </div>
                    <div v-if="event.location" class="flex items-center gap-1">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {{ event.location }}
                    </div>
                  </div>
                  <Link :href="route('events.show', event.id)">
                    <Button variant="outline" class="w-full group-hover:bg-accent group-hover:text-primary group-hover:border-accent">
                      En savoir plus
                    </Button>
                  </Link>
                </div>
              </Card>
            </div>
          </div>

          <!-- No Events Message -->
          <div v-else class="text-center py-12" data-aos="fade-up">
            <p class="text-muted-foreground text-lg">Aucun événement à venir pour le moment</p>
          </div>

          <!-- View All Button -->
          <div class="text-center" data-aos="fade-up" data-aos-delay="300">
            <Link :href="route('events.index')">
              <Button size="lg" class="px-8">
                Voir tous les événements
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <!-- Reviews Section -->
      <section class="py-24 px-4 bg-muted/30">
        <div class="max-w-7xl mx-auto">
          <!-- Section Header -->
          <div class="text-center mb-16">
            <p class="text-white text-sm uppercase tracking-widest mb-4 font-serif" data-aos="fade-up">Ce que disent nos clients</p>
            <h2 class="text-4xl md:text-5xl font-bold text-white mb-6 flex items-center justify-center gap-4 font-serif" data-aos="fade-up" data-aos-delay="100">
              <span class="h-px w-16 bg-accent"></span>
              <span>AVIS CLIENTS</span>
              <span class="h-px w-16 bg-accent"></span>
            </h2>
          </div>

          <!-- Reviews Carousel -->
          <div data-aos="fade-up" data-aos-delay="200">
            <ReviewsCarousel :reviews="reviews" />
          </div>
        </div>
      </section>

      <!-- Testimonial Section -->
      <section class="py-24 px-4">
        <div class="max-w-4xl mx-auto text-center">
          <!-- Quote Icon -->
          <div class="text-accent text-8xl mb-8 font-serif italic" data-aos="fade-down">"</div>

          <!-- Quote Text -->
          <blockquote class="text-foreground text-xl md:text-2xl leading-relaxed mb-12 italic" data-aos="fade-up" data-aos-delay="100">
            Faire appel à Lyra Events pour notre mariage a été la meilleure décision que nous ayons prise. L'équipe a su comprendre nos envies et les transformer en réalité, bien au-delà de nos espérances. De la coordination des prestataires à la gestion du jour J, tout était parfaitement orchestré.
            <br><br>
            La décoration était magnifique, le service traiteur exceptionnel, et l'ambiance exactement celle dont nous rêvions. Nos invités en parlent encore ! Un grand merci à toute l'équipe pour avoir créé des souvenirs inoubliables. Nous recommandons Lyra Events les yeux fermés.
          </blockquote>

          <!-- Author -->
          <p class="text-accent text-2xl font-serif" data-aos="fade-up" data-aos-delay="200">Sophie & Thomas, mariés en juin 2025</p>
        </div>
      </section>

      <!-- Footer -->
      <AppFooter />
  </div>
</template>

<script setup lang="ts">
import { Head, Link, usePage } from '@inertiajs/vue3'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import ReviewsCarousel from '@/components/ReviewsCarousel.vue'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ref, onMounted, onUnmounted, computed } from 'vue'
import AOS from 'aos'
import 'aos/dist/aos.css'

interface Event {
  id: number
  title: string
  description: string
  event_date: string
  event_end_date: string
  location: string | null
  image: string | null
  formatted_date: string
  formatted_time: string
}

interface Review {
  id: number
  author_name: string
  rating: number
  comment: string
  service?: string
  source: 'bark' | 'google' | 'facebook' | 'manual'
  review_date: string
  is_verified: boolean
}

interface Props {
  upcomingEvents: Event[]
  reviews: Review[]
}

defineProps<Props>()

const page = usePage()
const isScrolled = ref(false)
const mobileMenuOpen = ref(false)
const isLoggedIn = computed(() => !!(page.props as any).auth?.user)

const navLinks = [
  { label: 'Accueil', href: 'home', isRoute: true },
  { label: 'Le bar', href: 'bar.index', isRoute: true },
  { label: 'Événements', href: 'events.index', isRoute: true },
  { label: 'Contact', href: 'contact.create', isRoute: true },
]

const aboutImages = [
  { src: '/images/about-1.jpg', alt: 'Table mariage' },
  { src: '/images/about-2.jpg', alt: 'Main alliance' },
]

const navLinkClass = 'text-white text-2xl font-semibold hover:text-white/80 transition-colors font-serif'

const handleScroll = () => {
  isScrolled.value = window.scrollY > 100
}

onMounted(() => {
  // Initialize AOS
  AOS.init({
    duration: 800,
    easing: 'ease-out',
    once: true,
    offset: 100,
  })

  window.addEventListener('scroll', handleScroll)
  handleScroll()

  // Add structured data for video SEO
  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": "Lyra Events - Le mariage, l'événement le plus important de la vie",
    "description": "Reportage CNews présentant Lyra Events, agence spécialisée dans l'organisation de mariages et d'événements sur-mesure en France",
    "thumbnailUrl": "https://i.ytimg.com/vi/QlRrVoe1CJg/maxresdefault.jpg",
    "uploadDate": "2024-09-12",
    "contentUrl": "https://www.youtube.com/watch?v=QlRrVoe1CJg",
    "embedUrl": "https://www.youtube.com/embed/QlRrVoe1CJg",
    "publisher": {
      "@type": "Organization",
      "name": "Lyra Events",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/logo.svg`
      }
    }
  }

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.text = JSON.stringify(videoSchema)
  document.head.appendChild(script)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>
